import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import CustomSelect from '../components/CustomSelect';
import { avaliacoesService, lojasService, rotuloFormaPagamento } from '../services/api';
import faceInsatisfeito from '../assets/avaliacoes/insatisfeito.svg';
import facePoucoSatisfeito from '../assets/avaliacoes/pouco-satisfeito.svg';
import faceSatisfeito from '../assets/avaliacoes/satisfeito.svg';
import faceMuitoSatisfeito from '../assets/avaliacoes/muito-satisfeito.svg';
import '../styles/ConsultaGlobal.css';

/* ============================================================
   Avaliações de Atendimento — SOMENTE CONSULTA
   Fonte: pilotself.admcad1_venda (CLIENTE = 2)
   AVALIACAO 1..4 = carinha escolhida no totem · 0 = não avaliou
   VENDEDOR = 1   = venda espontânea (sem vendedor vinculado)
   ============================================================ */

const VENDEDOR_ESPONTANEO = 1;

const NOTAS = [
  {
    nota: 1,
    titulo: 'Insatisfeito',
    descricao: 'Experiência ruim',
    cor: '#FF3B30',
    corClara: '#FF8A80',
    corSuave: 'rgba(255, 59, 48, 0.12)',
    face: faceInsatisfeito
  },
  {
    nota: 2,
    titulo: 'Pouco satisfeito',
    descricao: 'Deixou a desejar',
    cor: '#FF9500',
    corClara: '#FFC061',
    corSuave: 'rgba(255, 149, 0, 0.14)',
    face: facePoucoSatisfeito
  },
  {
    nota: 3,
    titulo: 'Satisfeito',
    descricao: 'Gostou do atendimento',
    cor: '#32ADE6',
    corClara: '#7FD3F5',
    corSuave: 'rgba(50, 173, 230, 0.14)',
    face: faceSatisfeito
  },
  {
    nota: 4,
    titulo: 'Muito satisfeito',
    descricao: 'Superou as expectativas',
    cor: '#34C759',
    corClara: '#7BE39A',
    corSuave: 'rgba(52, 199, 89, 0.14)',
    face: faceMuitoSatisfeito
  }
];

const NOTA_POR_VALOR = NOTAS.reduce((acc, item) => {
  acc[item.nota] = item;
  return acc;
}, {});

const FaceAvaliacao = ({ nota, size = 44, className = '' }) => {
  const config = NOTA_POR_VALOR[nota];
  if (!config) return null;

  return (
    <img
      className={`aval-face ${className}`}
      src={config.face}
      alt={config.titulo}
      width={size}
      height={size}
      draggable="false"
    />
  );
};

/* ---------- utilidades ---------- */

const paraYmd = (data) => {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
};

const somarDias = (data, dias) => {
  const nova = new Date(data.getTime());
  nova.setDate(nova.getDate() + dias);
  return nova;
};

const PERIODOS = [
  { id: 'hoje', label: 'Hoje' },
  { id: '7dias', label: '7 dias' },
  { id: '30dias', label: '30 dias' },
  { id: 'mes', label: 'Mês atual' },
  { id: 'mesAnterior', label: 'Mês anterior' },
  { id: 'personalizado', label: 'Personalizado' }
];

const intervaloDoPeriodo = (periodoId) => {
  const hoje = new Date();
  switch (periodoId) {
    case 'hoje':
      return { dataIni: paraYmd(hoje), dataFim: paraYmd(hoje) };
    case '7dias':
      return { dataIni: paraYmd(somarDias(hoje, -6)), dataFim: paraYmd(hoje) };
    case '30dias':
      return { dataIni: paraYmd(somarDias(hoje, -29)), dataFim: paraYmd(hoje) };
    case 'mes':
      return {
        dataIni: paraYmd(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
        dataFim: paraYmd(hoje)
      };
    case 'mesAnterior':
      return {
        dataIni: paraYmd(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)),
        dataFim: paraYmd(new Date(hoje.getFullYear(), hoje.getMonth(), 0))
      };
    default:
      return { dataIni: paraYmd(somarDias(hoje, -6)), dataFim: paraYmd(hoje) };
  }
};

const TIPOS_VENDA = [
  { id: 'todos', label: 'Todas' },
  { id: 'vendedor', label: 'Com vendedor' },
  { id: 'espontanea', label: 'Espontâneas' }
];

const formatadorInteiro = new Intl.NumberFormat('pt-BR');
const formatadorMoeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarInteiro = (valor) => formatadorInteiro.format(Math.round(Number(valor) || 0));
const formatarMoeda = (valor) => formatadorMoeda.format(Number(valor) || 0);
const formatarPercentual = (valor, casas = 1) => `${(Number(valor) || 0).toFixed(casas).replace('.', ',')}%`;
const formatarMedia = (valor) => (Number(valor) || 0).toFixed(2).replace('.', ',');

const formatarDiaCurto = (ymd) => {
  const partes = String(ymd || '').split('-');
  if (partes.length !== 3) return ymd || '';
  return `${partes[2]}/${partes[1]}`;
};

const formatarDataBr = (ymd) => {
  const partes = String(ymd || '').split('-');
  if (partes.length !== 3) return ymd || '';
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

// A nota média (1 a 4) vira posição percentual no arco de satisfação.
const percentualDaMedia = (media) => {
  const valor = Number(media) || 0;
  if (valor <= 0) return 0;
  return Math.min(100, Math.max(0, ((valor - 1) / 3) * 100));
};

const notaDominante = (resumo) => {
  if (!resumo || !resumo.AVALIADAS) return null;
  return NOTAS.reduce((melhor, item) => (
    (resumo[`NOTA_${item.nota}`] || 0) > (resumo[`NOTA_${melhor.nota}`] || 0) ? item : melhor
  ), NOTAS[0]);
};

/* ---------- gráfico diário ---------- */

const GraficoDiario = ({ serie, modo, notasSelecionadas }) => {
  const [indiceAtivo, setIndiceAtivo] = useState(null);

  const maxAvaliadas = useMemo(
    () => serie.reduce((max, dia) => Math.max(max, dia.AVALIADAS || 0), 0),
    [serie]
  );

  if (serie.length === 0) return null;

  const totalDias = serie.length;
  const mostrarRotulo = (indice) => {
    const passo = Math.ceil(totalDias / 12);
    return indice % passo === 0 || indice === totalDias - 1;
  };

  // x no centro de cada coluna: a linha precisa passar pelo meio das barras.
  const posicaoColuna = (indice) => ((indice + 0.5) / totalDias) * 100;

  const pontosLinha = serie
    .map((dia, indice) => {
      const y = 100 - Math.min(100, Math.max(0, dia.SATISFACAO));
      return `${posicaoColuna(indice).toFixed(3)},${y.toFixed(3)}`;
    })
    .join(' ');

  const diaAtivo = indiceAtivo != null ? serie[indiceAtivo] : null;

  return (
    <div className="aval-chart">
      <div className="aval-chart-grid" aria-hidden="true">
        {[100, 75, 50, 25, 0].map((linha) => (
          <div key={linha} className="aval-chart-gridline">
            <span>{modo === 'proporcao' ? `${linha}%` : formatarInteiro((maxAvaliadas * linha) / 100)}</span>
          </div>
        ))}
      </div>

      <div className="aval-chart-plot">
        <svg
          className="aval-chart-line"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <polyline
            points={pontosLinha}
            fill="none"
            stroke="rgba(60, 60, 67, 0.55)"
            strokeWidth="1.6"
            strokeLinejoin="round"
            strokeLinecap="round"
            strokeDasharray="3 2"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        <div className="aval-chart-bars">
          {serie.map((dia, indice) => {
            const alturaColuna = modo === 'proporcao'
              ? (dia.AVALIADAS > 0 ? 100 : 0)
              : (maxAvaliadas > 0 ? ((dia.AVALIADAS || 0) / maxAvaliadas) * 100 : 0);

            return (
              <div
                key={dia.DIA}
                className={`aval-chart-col ${indiceAtivo === indice ? 'is-ativo' : ''}`}
                onMouseEnter={() => setIndiceAtivo(indice)}
                onMouseLeave={() => setIndiceAtivo((atual) => (atual === indice ? null : atual))}
                title={`${formatarDataBr(dia.DIA)} · ${formatarInteiro(dia.AVALIADAS)} avaliações`}
              >
                <div className="aval-chart-stack" style={{ height: `${alturaColuna}%` }}>
                  {[...NOTAS].reverse().map((item) => {
                    const quantidade = dia[`NOTA_${item.nota}`] || 0;
                    const proporcao = dia.AVALIADAS > 0 ? (quantidade / dia.AVALIADAS) * 100 : 0;
                    const apagada = notasSelecionadas.length > 0 && !notasSelecionadas.includes(item.nota);
                    return (
                      <div
                        key={item.nota}
                        className={`aval-chart-seg ${apagada ? 'is-apagada' : ''}`}
                        style={{ height: `${proporcao}%`, background: item.cor }}
                      />
                    );
                  })}
                </div>
                <span className={`aval-chart-label ${mostrarRotulo(indice) ? '' : 'is-oculto'}`}>
                  {formatarDiaCurto(dia.DIA)}
                </span>
              </div>
            );
          })}
        </div>

        {diaAtivo && (
          <div
            className="aval-chart-tooltip"
            style={{ left: `${posicaoColuna(indiceAtivo)}%` }}
          >
            <div className="aval-chart-tooltip-dia">{formatarDataBr(diaAtivo.DIA)}</div>
            <div className="aval-chart-tooltip-linha">
              <strong>{formatarInteiro(diaAtivo.AVALIADAS)}</strong> avaliações
              <span> · média {formatarMedia(diaAtivo.MEDIA)}</span>
            </div>
            {NOTAS.map((item) => (
              <div key={item.nota} className="aval-chart-tooltip-nota">
                <span className="aval-chart-tooltip-cor" style={{ background: item.cor }} />
                {item.titulo}
                <strong>{formatarInteiro(diaAtivo[`NOTA_${item.nota}`])}</strong>
              </div>
            ))}
            <div className="aval-chart-tooltip-rodape">
              Satisfação {formatarPercentual(diaAtivo.SATISFACAO)} · resposta {formatarPercentual(diaAtivo.TAXA_RESPOSTA)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------- barra de composição reaproveitada nos rankings ---------- */

const BarraComposicao = ({ linha }) => (
  <div className="aval-mini-barra" title={NOTAS.map((n) => `${n.titulo}: ${formatarInteiro(linha[`NOTA_${n.nota}`])}`).join(' · ')}>
    {NOTAS.map((item) => {
      const quantidade = linha[`NOTA_${item.nota}`] || 0;
      const proporcao = linha.AVALIADAS > 0 ? (quantidade / linha.AVALIADAS) * 100 : 0;
      if (proporcao <= 0) return null;
      return (
        <span
          key={item.nota}
          className="aval-mini-barra-seg"
          style={{ width: `${proporcao}%`, background: item.cor }}
        />
      );
    })}
  </div>
);

/* ---------- janela ---------- */

const Avaliacoes = () => {
  const periodoInicial = intervaloDoPeriodo('7dias');

  const [periodo, setPeriodo] = useState('7dias');
  const [dataIni, setDataIni] = useState(periodoInicial.dataIni);
  const [dataFim, setDataFim] = useState(periodoInicial.dataFim);
  const [loja, setLoja] = useState('');
  const [tipoVenda, setTipoVenda] = useState('todos');
  const [vendedor, setVendedor] = useState('');

  const [filtrosAtivos, setFiltrosAtivos] = useState({
    dataIni: periodoInicial.dataIni,
    dataFim: periodoInicial.dataFim,
    loja: '',
    tipoVenda: 'todos',
    vendedor: ''
  });

  const [lojas, setLojas] = useState([]);

  const [resumo, setResumo] = useState(null);
  const [serie, setSerie] = useState([]);
  const [rankingLojas, setRankingLojas] = useState([]);
  const [rankingVendedores, setRankingVendedores] = useState([]);
  const [lista, setLista] = useState([]);

  const [carregandoPainel, setCarregandoPainel] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(false);
  const [erro, setErro] = useState('');

  const [notasSelecionadas, setNotasSelecionadas] = useState([]);
  const [incluirNaoAvaliadas, setIncluirNaoAvaliadas] = useState(false);
  const [abaRanking, setAbaRanking] = useState('lojas');
  const [modoGrafico, setModoGrafico] = useState('volume');
  const [ordemRanking, setOrdemRanking] = useState('satisfacao');

  const [vendaSelecionada, setVendaSelecionada] = useState(null);
  const [itensVenda, setItensVenda] = useState([]);
  const [carregandoItens, setCarregandoItens] = useState(false);

  const notasKey = notasSelecionadas.slice().sort().join(',');

  useEffect(() => {
    lojasService
      .getLojas()
      .then((resposta) => setLojas((resposta || []).slice().sort((a, b) => a.numero - b.numero)))
      .catch((err) => console.error('Erro ao carregar lojas:', err));
  }, []);

  const nomeLoja = useCallback((numero) => {
    const encontrada = lojas.find((item) => Number(item.numero) === Number(numero));
    return encontrada ? encontrada.nome : `Loja ${numero}`;
  }, [lojas]);

  // Painel (KPIs, gráfico e rankings) ignora a seleção de carinhas: ela é o
  // recorte da lista analítica, não do panorama.
  useEffect(() => {
    let cancelado = false;

    const carregarPainel = async () => {
      setCarregandoPainel(true);
      setErro('');
      try {
        const [dadosResumo, dadosSerie, dadosLojas, dadosVendedores] = await Promise.all([
          avaliacoesService.resumo(filtrosAtivos),
          avaliacoesService.serie(filtrosAtivos),
          avaliacoesService.porLoja(filtrosAtivos),
          avaliacoesService.porVendedor(filtrosAtivos)
        ]);
        if (cancelado) return;
        setResumo(dadosResumo);
        setSerie(dadosSerie);
        setRankingLojas(dadosLojas);
        setRankingVendedores(dadosVendedores);
      } catch (err) {
        if (cancelado) return;
        console.error('Erro ao carregar avaliações:', err);
        setErro(err?.message || 'Não foi possível carregar as avaliações.');
        setResumo(null);
        setSerie([]);
        setRankingLojas([]);
        setRankingVendedores([]);
      } finally {
        if (!cancelado) setCarregandoPainel(false);
      }
    };

    carregarPainel();
    return () => { cancelado = true; };
  }, [filtrosAtivos]);

  useEffect(() => {
    let cancelado = false;

    const carregarLista = async () => {
      setCarregandoLista(true);
      try {
        const dados = await avaliacoesService.listar({
          ...filtrosAtivos,
          notas: notasKey ? notasKey.split(',').map(Number) : [],
          somenteAvaliadas: !incluirNaoAvaliadas
        });
        if (!cancelado) setLista(dados);
      } catch (err) {
        if (!cancelado) {
          console.error('Erro ao listar avaliações:', err);
          setLista([]);
        }
      } finally {
        if (!cancelado) setCarregandoLista(false);
      }
    };

    carregarLista();
    return () => { cancelado = true; };
  }, [filtrosAtivos, notasKey, incluirNaoAvaliadas]);

  // Os controles segmentados valem na hora; datas, loja e matrícula só entram
  // ao confirmar, para não disparar quatro consultas a cada tecla digitada.
  const aplicar = useCallback((sobrescritas = {}) => {
    setErro('');
    setFiltrosAtivos({
      dataIni,
      dataFim,
      loja,
      tipoVenda,
      vendedor: vendedor.trim(),
      ...sobrescritas
    });
  }, [dataIni, dataFim, loja, tipoVenda, vendedor]);

  const aplicarPeriodo = (periodoId) => {
    setPeriodo(periodoId);
    if (periodoId === 'personalizado') return;
    const intervalo = intervaloDoPeriodo(periodoId);
    setDataIni(intervalo.dataIni);
    setDataFim(intervalo.dataFim);
    aplicar(intervalo);
  };

  const aplicarTipoVenda = (tipoId) => {
    setTipoVenda(tipoId);
    aplicar({ tipoVenda: tipoId });
  };

  const aplicarFiltros = (evento) => {
    if (evento) evento.preventDefault();
    if (dataIni > dataFim) {
      setErro('A data inicial não pode ser maior que a final.');
      return;
    }
    aplicar();
  };

  const alternarNota = (nota) => {
    setNotasSelecionadas((atual) => (
      atual.includes(nota) ? atual.filter((item) => item !== nota) : [...atual, nota].sort()
    ));
  };

  const abrirDetalhes = async (venda) => {
    setVendaSelecionada(venda);
    setItensVenda([]);
    setCarregandoItens(true);
    try {
      const itens = await avaliacoesService.itensVenda(venda.ID);
      setItensVenda(itens);
    } catch (err) {
      console.error('Erro ao buscar itens da venda:', err);
      setItensVenda([]);
    } finally {
      setCarregandoItens(false);
    }
  };

  const fecharDetalhes = () => {
    setVendaSelecionada(null);
    setItensVenda([]);
  };

  const dominante = notaDominante(resumo);

  const rankingOrdenado = useMemo(() => {
    const base = abaRanking === 'lojas' ? rankingLojas : rankingVendedores;
    const copia = base.filter((linha) => linha.AVALIADAS > 0);
    if (ordemRanking === 'volume') {
      copia.sort((a, b) => b.AVALIADAS - a.AVALIADAS);
    } else if (ordemRanking === 'criticas') {
      copia.sort((a, b) => (b.NOTA_1 + b.NOTA_2) - (a.NOTA_1 + a.NOTA_2));
    } else {
      copia.sort((a, b) => b.SATISFACAO - a.SATISFACAO || b.AVALIADAS - a.AVALIADAS);
    }
    return copia;
  }, [abaRanking, rankingLojas, rankingVendedores, ordemRanking]);

  const opcoesLojas = useMemo(() => ([
    { value: '', label: 'Todas as lojas' },
    ...lojas.map((item) => ({ value: String(item.numero), label: `${item.numero} - ${item.nome}` }))
  ]), [lojas]);

  const totalRegistrosLista = lista.length;

  return (
    <div className="aval-page">
      <header className="aval-page-header">
        <div>
          <span className="aval-page-eyebrow">Vendas e pós-venda</span>
          <h2>Avaliações de Atendimento</h2>
          <p>Carinhas registradas pelos clientes no totem de autoatendimento.</p>
        </div>
      </header>

      <div className="aval-root">
        {/* ---------- filtros ---------- */}
        <form className="aval-filtros" onSubmit={aplicarFiltros}>
          <div className="aval-filtros-topo">
            <div className="aval-segmented" role="group" aria-label="Período">
              {PERIODOS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`aval-segmented-btn ${periodo === item.id ? 'is-ativo' : ''}`}
                  onClick={() => aplicarPeriodo(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="aval-segmented" role="group" aria-label="Tipo de venda">
              {TIPOS_VENDA.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`aval-segmented-btn ${tipoVenda === item.id ? 'is-ativo' : ''}`}
                  onClick={() => aplicarTipoVenda(item.id)}
                  title={item.id === 'espontanea' ? 'Vendas sem vendedor vinculado (matrícula 1)' : undefined}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="aval-filtros-linha">
            <div className="aval-campo">
              <label htmlFor="aval-data-ini">De</label>
              <input
                id="aval-data-ini"
                type="date"
                value={dataIni}
                onChange={(e) => { setDataIni(e.target.value); setPeriodo('personalizado'); }}
              />
            </div>
            <div className="aval-campo">
              <label htmlFor="aval-data-fim">Até</label>
              <input
                id="aval-data-fim"
                type="date"
                value={dataFim}
                onChange={(e) => { setDataFim(e.target.value); setPeriodo('personalizado'); }}
              />
            </div>
            <div className="aval-campo aval-campo--loja">
              <label>Loja</label>
              <CustomSelect
                options={opcoesLojas}
                value={loja}
                onChange={setLoja}
                placeholder="Todas as lojas"
                searchPlaceholder="Buscar loja..."
              />
            </div>
            <div className="aval-campo">
              <label htmlFor="aval-vendedor">Matrícula do vendedor</label>
              <input
                id="aval-vendedor"
                type="text"
                inputMode="numeric"
                value={vendedor}
                onChange={(e) => setVendedor(e.target.value.replace(/\D/g, ''))}
                placeholder="Opcional"
              />
            </div>
            <button type="submit" className="aval-btn-aplicar" disabled={carregandoPainel}>
              <i className={`fas ${carregandoPainel ? 'fa-spinner fa-spin' : 'fa-arrows-rotate'}`}></i>
              Atualizar
            </button>
          </div>

          <div className="aval-filtros-info">
            <i className="fas fa-circle-info"></i>
            Período de {formatarDataBr(filtrosAtivos.dataIni)} a {formatarDataBr(filtrosAtivos.dataFim)}
            {filtrosAtivos.loja ? ` · ${nomeLoja(filtrosAtivos.loja)}` : ' · todas as lojas'}
            {filtrosAtivos.vendedor ? ` · matrícula ${filtrosAtivos.vendedor}` : ''}
            {filtrosAtivos.tipoVenda === 'espontanea' ? ' · somente espontâneas' : ''}
            {filtrosAtivos.tipoVenda === 'vendedor' ? ' · somente com vendedor' : ''}
          </div>
        </form>

        {erro && (
          <div className="consulta-error">
            <i className="fas fa-triangle-exclamation"></i> {erro}
          </div>
        )}

        {carregandoPainel && !resumo && (
          <div className="consulta-loading">
            <i className="fas fa-spinner fa-spin"></i> Carregando avaliações...
          </div>
        )}

        {resumo && (
          <>
            {/* ---------- destaque + KPIs ---------- */}
            <section className={`aval-hero ${carregandoPainel ? 'is-carregando' : ''}`}>
              <div className="aval-hero-media">
                <div className="aval-hero-face">
                  <FaceAvaliacao nota={dominante ? dominante.nota : 4} size={92} />
                </div>
                <div className="aval-hero-media-texto">
                  <span className="aval-hero-rotulo">Nota média</span>
                  <div className="aval-hero-valor">
                    {formatarMedia(resumo.MEDIA)}
                    <small>/ 4</small>
                  </div>
                  <div className="aval-hero-escala">
                    <div className="aval-hero-escala-trilho">
                      <div
                        className="aval-hero-escala-preenchida"
                        style={{ width: `${percentualDaMedia(resumo.MEDIA)}%` }}
                      />
                      <div
                        className="aval-hero-escala-marcador"
                        style={{ left: `${percentualDaMedia(resumo.MEDIA)}%` }}
                      />
                    </div>
                    <div className="aval-hero-escala-legenda">
                      <span>Insatisfeito</span>
                      <span>Muito satisfeito</span>
                    </div>
                  </div>
                  {dominante && (
                    <span className="aval-hero-dominante">
                      Resposta mais frequente: <strong>{dominante.titulo}</strong>
                    </span>
                  )}
                </div>
              </div>

              <div className="aval-kpis">
                <div className="aval-kpi">
                  <span className="aval-kpi-rotulo">Satisfação</span>
                  <strong className="aval-kpi-valor" style={{ color: 'var(--ios-green)' }}>
                    {formatarPercentual(resumo.SATISFACAO)}
                  </strong>
                  <span className="aval-kpi-nota">
                    {formatarInteiro(resumo.NOTA_3 + resumo.NOTA_4)} de {formatarInteiro(resumo.AVALIADAS)} positivas
                  </span>
                </div>
                <div className="aval-kpi">
                  <span className="aval-kpi-rotulo">Avaliações</span>
                  <strong className="aval-kpi-valor">{formatarInteiro(resumo.AVALIADAS)}</strong>
                  <span className="aval-kpi-nota">
                    {formatarInteiro(resumo.NOTA_1 + resumo.NOTA_2)} negativas
                  </span>
                </div>
                <div className="aval-kpi">
                  <span className="aval-kpi-rotulo">Taxa de resposta</span>
                  <strong className="aval-kpi-valor">{formatarPercentual(resumo.TAXA_RESPOSTA)}</strong>
                  <span className="aval-kpi-nota">
                    sobre {formatarInteiro(resumo.TOTAL_VENDAS)} vendas
                  </span>
                </div>
                <div className="aval-kpi">
                  <span className="aval-kpi-rotulo">Vendas espontâneas</span>
                  <strong className="aval-kpi-valor">
                    {resumo.TOTAL_VENDAS > 0
                      ? formatarPercentual((resumo.ESPONTANEAS / resumo.TOTAL_VENDAS) * 100)
                      : '—'}
                  </strong>
                  <span className="aval-kpi-nota">
                    {formatarInteiro(resumo.ESPONTANEAS)} sem vendedor
                  </span>
                </div>
                <div className="aval-kpi">
                  <span className="aval-kpi-rotulo">Valor avaliado</span>
                  <strong className="aval-kpi-valor">{formatarMoeda(resumo.VALOR_AVALIADO)}</strong>
                  <span className="aval-kpi-nota">
                    de {formatarMoeda(resumo.VALOR_TOTAL)} vendidos
                  </span>
                </div>
              </div>
            </section>

            {/* ---------- distribuição / seleção ---------- */}
            <section className="aval-bloco">
              <header className="aval-bloco-header">
                <h4>
                  <i className="fas fa-chart-simple"></i> Distribuição das respostas
                </h4>
                <span className="aval-bloco-dica">
                  {notasSelecionadas.length > 0
                    ? `Filtrando a lista por ${notasSelecionadas.length} carinha(s)`
                    : 'Clique em uma carinha para filtrar a lista'}
                </span>
              </header>

              <div className="aval-cards-notas">
                {NOTAS.map((item) => {
                  const quantidade = resumo[`NOTA_${item.nota}`] || 0;
                  const proporcao = resumo.AVALIADAS > 0 ? (quantidade / resumo.AVALIADAS) * 100 : 0;
                  const selecionada = notasSelecionadas.includes(item.nota);
                  return (
                    <button
                      key={item.nota}
                      type="button"
                      className={`aval-card-nota ${selecionada ? 'is-selecionada' : ''}`}
                      style={{ '--nota-cor': item.cor, '--nota-suave': item.corSuave }}
                      onClick={() => alternarNota(item.nota)}
                      aria-pressed={selecionada}
                    >
                      <FaceAvaliacao nota={item.nota} size={52} />
                      <div className="aval-card-nota-info">
                        <span className="aval-card-nota-titulo">{item.titulo}</span>
                        <span className="aval-card-nota-desc">{item.descricao}</span>
                        <div className="aval-card-nota-numeros">
                          <strong>{formatarInteiro(quantidade)}</strong>
                          <span>{formatarPercentual(proporcao)}</span>
                        </div>
                        <div className="aval-card-nota-barra">
                          <span style={{ width: `${proporcao}%` }} />
                        </div>
                      </div>
                      {selecionada && (
                        <i className="fas fa-circle-check aval-card-nota-check" aria-hidden="true"></i>
                      )}
                    </button>
                  );
                })}
              </div>

              {notasSelecionadas.length > 0 && (
                <button
                  type="button"
                  className="aval-limpar-selecao"
                  onClick={() => setNotasSelecionadas([])}
                >
                  <i className="fas fa-xmark"></i> Limpar seleção
                </button>
              )}
            </section>

            {/* ---------- gráfico ---------- */}
            {serie.length > 0 && (
              <section className="aval-bloco">
                <header className="aval-bloco-header">
                  <h4>
                    <i className="fas fa-chart-column"></i> Evolução diária
                  </h4>
                  <div className="aval-segmented aval-segmented--mini">
                    <button
                      type="button"
                      className={`aval-segmented-btn ${modoGrafico === 'volume' ? 'is-ativo' : ''}`}
                      onClick={() => setModoGrafico('volume')}
                    >
                      Volume
                    </button>
                    <button
                      type="button"
                      className={`aval-segmented-btn ${modoGrafico === 'proporcao' ? 'is-ativo' : ''}`}
                      onClick={() => setModoGrafico('proporcao')}
                    >
                      Proporção
                    </button>
                  </div>
                </header>

                <GraficoDiario serie={serie} modo={modoGrafico} notasSelecionadas={notasSelecionadas} />

                <div className="aval-legenda">
                  {NOTAS.map((item) => (
                    <span key={item.nota} className="aval-legenda-item">
                      <span className="aval-legenda-cor" style={{ background: item.cor }} />
                      {item.titulo}
                    </span>
                  ))}
                  <span className="aval-legenda-item">
                    <span className="aval-legenda-linha" />
                    % de satisfação
                  </span>
                </div>
              </section>
            )}

            {/* ---------- rankings ---------- */}
            <section className="aval-bloco">
              <header className="aval-bloco-header">
                <div className="aval-segmented aval-segmented--mini">
                  <button
                    type="button"
                    className={`aval-segmented-btn ${abaRanking === 'lojas' ? 'is-ativo' : ''}`}
                    onClick={() => setAbaRanking('lojas')}
                  >
                    <i className="fas fa-store"></i> Por loja
                  </button>
                  <button
                    type="button"
                    className={`aval-segmented-btn ${abaRanking === 'vendedores' ? 'is-ativo' : ''}`}
                    onClick={() => setAbaRanking('vendedores')}
                  >
                    <i className="fas fa-user-tag"></i> Por vendedor
                  </button>
                </div>

                <div className="aval-segmented aval-segmented--mini">
                  <button
                    type="button"
                    className={`aval-segmented-btn ${ordemRanking === 'satisfacao' ? 'is-ativo' : ''}`}
                    onClick={() => setOrdemRanking('satisfacao')}
                  >
                    Melhores
                  </button>
                  <button
                    type="button"
                    className={`aval-segmented-btn ${ordemRanking === 'criticas' ? 'is-ativo' : ''}`}
                    onClick={() => setOrdemRanking('criticas')}
                  >
                    Mais críticas
                  </button>
                  <button
                    type="button"
                    className={`aval-segmented-btn ${ordemRanking === 'volume' ? 'is-ativo' : ''}`}
                    onClick={() => setOrdemRanking('volume')}
                  >
                    Volume
                  </button>
                </div>
              </header>

              {rankingOrdenado.length === 0 ? (
                <div className="consulta-placeholder">
                  <i className="fas fa-inbox"></i>
                  <p>Sem avaliações no período para este recorte.</p>
                </div>
              ) : (
                <div className="aval-tabela-wrap aval-tabela-wrap--ranking">
                  <table className="aval-tabela">
                    <thead>
                      <tr>
                        <th className="aval-col-pos">#</th>
                        <th>{abaRanking === 'lojas' ? 'Loja' : 'Vendedor'}</th>
                        <th className="aval-col-num">Avaliações</th>
                        <th className="aval-col-num">Média</th>
                        <th className="aval-col-num">Satisfação</th>
                        <th className="aval-col-num">Resposta</th>
                        <th className="aval-col-barra">Composição</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankingOrdenado.map((linha, indice) => {
                        const identificador = abaRanking === 'lojas' ? linha.LOJA : linha.VENDEDOR;
                        const rotulo = abaRanking === 'lojas'
                          ? nomeLoja(linha.LOJA)
                          : (linha.NOME || `Matrícula ${linha.VENDEDOR}`);
                        return (
                          <tr key={identificador}>
                            <td className="aval-col-pos">{indice + 1}</td>
                            <td>
                              <div className="aval-ranking-nome">
                                <strong>{rotulo}</strong>
                                <span>{abaRanking === 'lojas' ? `Filial ${linha.LOJA}` : `Matrícula ${linha.VENDEDOR}`}</span>
                              </div>
                            </td>
                            <td className="aval-col-num">{formatarInteiro(linha.AVALIADAS)}</td>
                            <td className="aval-col-num">
                              <span className="aval-pill-media">{formatarMedia(linha.MEDIA)}</span>
                            </td>
                            <td className="aval-col-num aval-satisfacao">{formatarPercentual(linha.SATISFACAO)}</td>
                            <td className="aval-col-num aval-suave">{formatarPercentual(linha.TAXA_RESPOSTA)}</td>
                            <td className="aval-col-barra"><BarraComposicao linha={linha} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* ---------- lista analítica ---------- */}
            <section className="aval-bloco">
              <header className="aval-bloco-header">
                <h4>
                  <i className="fas fa-list-ul"></i> Avaliações
                  <span className="aval-contador">{formatarInteiro(totalRegistrosLista)}</span>
                </h4>
                <label className="aval-check">
                  <input
                    type="checkbox"
                    checked={incluirNaoAvaliadas}
                    onChange={(e) => setIncluirNaoAvaliadas(e.target.checked)}
                  />
                  Incluir vendas não avaliadas
                </label>
              </header>

              {carregandoLista ? (
                <div className="consulta-loading">
                  <i className="fas fa-spinner fa-spin"></i> Carregando lista...
                </div>
              ) : lista.length === 0 ? (
                <div className="consulta-placeholder">
                  <i className="fas fa-face-meh-blank"></i>
                  <p>Nenhum registro encontrado com os filtros atuais.</p>
                </div>
              ) : (
                <>
                  <div className="aval-tabela-wrap">
                    <table className="aval-tabela aval-tabela--lista">
                      <thead>
                        <tr>
                          <th className="aval-col-face">Nota</th>
                          <th>Data / hora</th>
                          <th>Loja</th>
                          <th className="aval-col-num">Cupom</th>
                          <th>Vendedor</th>
                          <th className="aval-col-num">Itens</th>
                          <th className="aval-col-num">Valor</th>
                          <th>Pagamento</th>
                          <th className="aval-col-acao"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lista.map((venda) => {
                          const config = NOTA_POR_VALOR[venda.AVALIACAO];
                          const espontanea = venda.VENDEDOR === VENDEDOR_ESPONTANEO;
                          return (
                            <tr
                              key={venda.ID}
                              className="aval-linha"
                              onDoubleClick={() => abrirDetalhes(venda)}
                              title="Clique duas vezes para ver os detalhes"
                            >
                              <td className="aval-col-face">
                                {config ? (
                                  <div className="aval-linha-nota">
                                    <FaceAvaliacao nota={venda.AVALIACAO} size={26} />
                                    <span style={{ color: config.cor }}>{config.titulo}</span>
                                  </div>
                                ) : (
                                  <span className="aval-sem-nota">Não avaliou</span>
                                )}
                              </td>
                              <td className="aval-mono">{venda.DATA_HORA}</td>
                              <td>
                                <div className="aval-ranking-nome">
                                  <strong>{nomeLoja(venda.LOJA)}</strong>
                                  <span>Filial {venda.LOJA} · caixa {venda.CAIXA}</span>
                                </div>
                              </td>
                              <td className="aval-col-num aval-mono">{venda.NFCE}</td>
                              <td>
                                {espontanea ? (
                                  <span className="aval-tag-espontanea">
                                    <i className="fas fa-bolt"></i> Espontânea
                                  </span>
                                ) : (
                                  <div className="aval-ranking-nome">
                                    <strong>{venda.VENDEDOR_NOME || `Matrícula ${venda.VENDEDOR}`}</strong>
                                    <span>Matrícula {venda.VENDEDOR}</span>
                                  </div>
                                )}
                              </td>
                              <td className="aval-col-num">{formatarInteiro(venda.TOTAL_PRODUTOS)}</td>
                              <td className="aval-col-num aval-valor">{formatarMoeda(venda.VALOR_TOTAL)}</td>
                              <td className="aval-col-pg">{rotuloFormaPagamento(venda.FORMAPG)}</td>
                              <td className="aval-col-acao">
                                <button
                                  type="button"
                                  className="aval-btn-detalhe"
                                  onClick={() => abrirDetalhes(venda)}
                                >
                                  Detalhes
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {totalRegistrosLista >= 400 && (
                    <p className="aval-aviso-limite">
                      <i className="fas fa-circle-info"></i>
                      Exibindo as 400 avaliações mais recentes do período. Refine o filtro para ver as demais.
                    </p>
                  )}
                </>
              )}
            </section>
          </>
        )}
      </div>

      {/* ---------- detalhe da venda ---------- */}
      {vendaSelecionada && createPortal(
        <div className="aval-sheet-backdrop" onClick={fecharDetalhes}>
          <div
            className="aval-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Detalhes da avaliação"
          >
            <header
              className="aval-sheet-header"
              style={{
                '--nota-cor': NOTA_POR_VALOR[vendaSelecionada.AVALIACAO]?.cor || 'var(--gray-1)',
                '--nota-suave': NOTA_POR_VALOR[vendaSelecionada.AVALIACAO]?.corSuave || 'var(--fill-4)'
              }}
            >
              <div className="aval-sheet-nota">
                {NOTA_POR_VALOR[vendaSelecionada.AVALIACAO] ? (
                  <>
                    <FaceAvaliacao nota={vendaSelecionada.AVALIACAO} size={62} />
                    <div>
                      <div className="aval-sheet-nota-titulo">
                        {NOTA_POR_VALOR[vendaSelecionada.AVALIACAO].titulo}
                      </div>
                      <div className="aval-sheet-nota-sub">
                        Nota {vendaSelecionada.AVALIACAO} de 4 · {vendaSelecionada.DATA_HORA}
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    <div className="aval-sheet-nota-titulo">Venda não avaliada</div>
                    <div className="aval-sheet-nota-sub">{vendaSelecionada.DATA_HORA}</div>
                  </div>
                )}
              </div>
              <button type="button" className="aval-sheet-close" onClick={fecharDetalhes} aria-label="Fechar">
                <i className="fas fa-times"></i>
              </button>
            </header>

            <div className="aval-sheet-body">
              <div className="aval-sheet-grid">
                <div className="aval-sheet-item">
                  <span>Loja</span>
                  <strong>{nomeLoja(vendaSelecionada.LOJA)}</strong>
                </div>
                <div className="aval-sheet-item">
                  <span>Filial / caixa</span>
                  <strong>{vendaSelecionada.LOJA} · {vendaSelecionada.CAIXA}</strong>
                </div>
                <div className="aval-sheet-item">
                  <span>NFC-e</span>
                  <strong>{vendaSelecionada.NFCE} / série {vendaSelecionada.SERIE}</strong>
                </div>
                <div className="aval-sheet-item">
                  <span>Vendedor</span>
                  <strong>
                    {vendaSelecionada.VENDEDOR === VENDEDOR_ESPONTANEO
                      ? 'Venda espontânea'
                      : (vendaSelecionada.VENDEDOR_NOME || `Matrícula ${vendaSelecionada.VENDEDOR}`)}
                  </strong>
                </div>
                <div className="aval-sheet-item">
                  <span>Forma de pagamento</span>
                  <strong>{rotuloFormaPagamento(vendaSelecionada.FORMAPG)}</strong>
                </div>
                <div className="aval-sheet-item">
                  <span>Valor total</span>
                  <strong>{formatarMoeda(vendaSelecionada.VALOR_TOTAL)}</strong>
                </div>
                <div className="aval-sheet-item">
                  <span>Desconto</span>
                  <strong>{formatarMoeda(vendaSelecionada.VALOR_DESCONTO)}</strong>
                </div>
                <div className="aval-sheet-item">
                  <span>Pré-venda</span>
                  <strong>{vendaSelecionada.PREVENDA || '—'}</strong>
                </div>
              </div>

              {vendaSelecionada.NFCE_CHAVE && (
                <div className="aval-sheet-chave">
                  <span>Chave NFC-e</span>
                  <code>{vendaSelecionada.NFCE_CHAVE}</code>
                </div>
              )}

              <h5 className="aval-sheet-subtitulo">
                <i className="fas fa-basket-shopping"></i> Itens do cupom
              </h5>

              {carregandoItens ? (
                <div className="consulta-loading">
                  <i className="fas fa-spinner fa-spin"></i> Buscando itens...
                </div>
              ) : itensVenda.length === 0 ? (
                <div className="consulta-placeholder">
                  <i className="fas fa-box-open"></i>
                  <p>Nenhum item encontrado para esta venda.</p>
                </div>
              ) : (
                <div className="aval-tabela-wrap">
                  <table className="aval-tabela">
                    <thead>
                      <tr>
                        <th className="aval-col-num">Código</th>
                        <th>Produto</th>
                        <th className="aval-col-num">Qtd</th>
                        <th className="aval-col-num">Desconto</th>
                        <th className="aval-col-num">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itensVenda.map((item, indice) => (
                        <tr key={`${item.PRODUTO}-${indice}`}>
                          <td className="aval-col-num aval-mono">{item.PRODUTO}</td>
                          <td>{item.NOME || 'Produto sem descrição'}</td>
                          <td className="aval-col-num">{item.QTD}</td>
                          <td className="aval-col-num aval-suave">{formatarMoeda(item.VALOR_DESCONTO)}</td>
                          <td className="aval-col-num aval-valor">{formatarMoeda(item.SUBTOTAL)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Avaliacoes;
